import React, { useState, useRef, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Grid,
    Paper,
    Chip,
    Card,
    CardContent
} from '@mui/material';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// 青春期變化數據
const teenChanges = [
    { id: 1, text: "身高和體重顯著增加", category: "both", color: "#4caf50" },
    { id: 2, text: "容易長出暗瘡", category: "both", color: "#4caf50" },
    { id: 3, text: "長出腋毛和陰毛", category: "both", color: "#4caf50" },
    { id: 4, text: "生殖器官逐漸成熟", category: "both", color: "#4caf50" },
    { id: 5, text: "臀部變得渾圓", category: "girls", color: "#e91e63" },
    { id: 6, text: "出現月經", category: "girls", color: "#e91e63" },
    { id: 7, text: "乳房隆起", category: "girls", color: "#e91e63" },
    { id: 8, text: "肌肉變得發達", category: "boys", color: "#2196f3" },
    { id: 9, text: "可能出現夢遺", category: "boys", color: "#2196f3" },
    { id: 10, text: "長出喉結，聲調變得低沉", category: "boys", color: "#2196f3" },
    { id: 11, text: "長出腿毛", category: "boys", color: "#2196f3" },
    { id: 12, text: "長出鬍鬚", category: "boys", color: "#2196f3" }
];

interface DraggableItem {
    id: number;
    text: string;
    category: string;
    color: string;
    x: number;
    y: number;
    isDragging: boolean;
    isPlaced: boolean;
    placedIn: string | null;
    staticX?: number;
    staticY?: number;
}

interface CircleInfo {
    x: number;
    y: number;
    radius: number;
    category: string;
}

export function PageGSQuiz() {
    const [items, setItems] = useState<DraggableItem[]>(() => 
        teenChanges.map((item, index) => ({
            ...item,
            x: 50 + (index % 4) * 200,
            y: 50 + Math.floor(index / 4) * 80,
            isDragging: false,
            isPlaced: false,
            placedIn: null
        }))
    );
    
    const [draggedItem, setDraggedItem] = useState<DraggableItem | null>(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [score, setScore] = useState(0);
    const [gameComplete, setGameComplete] = useState(false);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // 圓形配置 - 響應式設計
    const getCircleConfig = () => {
        const containerWidth = containerSize.width || 800;
        const containerHeight = containerSize.height || 600;
        
        const radius = Math.min(containerWidth, containerHeight) * 0.25 * 1.8; // 放大1.8倍
        const centerY = containerHeight / 2;
        const centerX = containerWidth / 2;
        
        return {
            boys: { x: centerX - radius * 0.5, y: centerY, radius, category: 'boys' },
            girls: { x: centerX + radius * 0.5, y: centerY, radius, category: 'girls' }
        };
    };

    // 檢查碰撞檢測 - 修復版本
    const checkCollision = (item: DraggableItem, x: number, y: number) => {
        const circles = getCircleConfig();
        const boysCircle = circles.boys;
        const girlsCircle = circles.girls;
        
        // 添加調試信息
        console.log('Checking collision:', {
            item: item.text,
            x, y,
            boysCircle: { x: boysCircle.x, y: boysCircle.y, radius: boysCircle.radius },
            girlsCircle: { x: girlsCircle.x, y: girlsCircle.y, radius: girlsCircle.radius }
        });
        
        // 計算項目中心點到圓形中心點的距離
        // 使用勾股定理: distance = sqrt((x2-x1)² + (y2-y1)²)
        const distanceToBoys = Math.sqrt((x - boysCircle.x) ** 2 + (y - boysCircle.y) ** 2);
        const distanceToGirls = Math.sqrt((x - girlsCircle.x) ** 2 + (y - girlsCircle.y) ** 2);
        
        console.log('Distances:', { distanceToBoys, distanceToGirls, boysRadius: boysCircle.radius, girlsRadius: girlsCircle.radius });
        
        // 檢查是否在男孩圓形內：距離 <= 半徑
        if (distanceToBoys <= boysCircle.radius) {
            console.log('Collision with boys circle!');
            return { circle: 'boys', distance: distanceToBoys };
        } 
        // 檢查是否在女孩圓形內：距離 <= 半徑
        else if (distanceToGirls <= girlsCircle.radius) {
            console.log('Collision with girls circle!');
            return { circle: 'girls', distance: distanceToGirls };
        }
        
        console.log('No collision');
        return null;
    };

    // 檢查是否在重疊區域
    const checkOverlap = (x: number, y: number) => {
        const circles = getCircleConfig();
        const boysCircle = circles.boys;
        const girlsCircle = circles.girls;
        
        // 計算到兩個圓形的距離
        const distanceToBoys = Math.sqrt((x - boysCircle.x) ** 2 + (y - boysCircle.y) ** 2);
        const distanceToGirls = Math.sqrt((x - girlsCircle.x) ** 2 + (y - girlsCircle.y) ** 2);
        
        // 重疊區域：同時在兩個圓形內
        const isOverlap = distanceToBoys <= boysCircle.radius && distanceToGirls <= girlsCircle.radius;
        console.log('Overlap check:', { x, y, distanceToBoys, distanceToGirls, boysRadius: boysCircle.radius, girlsRadius: girlsCircle.radius, isOverlap });
        
        return isOverlap;
    };

    // 獲取圓形上方排列位置 - 修復版本
    const getStaticPosition = (category: string, index: number) => {
        const circles = getCircleConfig();
        const boysCircle = circles.boys;
        const girlsCircle = circles.girls;
        
        const itemWidth = 120;
        const itemHeight = 40;
        const spacing = 10;
        
        let x, y;
        
        if (category === 'both') {
            // 重疊區域 - 在兩個圓形之間
            const centerX = (boysCircle.x + girlsCircle.y) / 2;
            const startY = Math.min(boysCircle.y, girlsCircle.y) - Math.min(boysCircle.radius, girlsCircle.radius) - 60;
            x = centerX - itemWidth / 2;
            y = startY - (index * (itemHeight + spacing));
        } else if (category === 'boys') {
            // 男孩圓形
            const startY = boysCircle.y - boysCircle.radius - 60;
            x = boysCircle.x - itemWidth / 2;
            y = startY - (index * (itemHeight + spacing));
        } else if (category === 'girls') {
            // 女孩圓形
            const startY = girlsCircle.y - girlsCircle.radius - 60;
            x = girlsCircle.x - itemWidth / 2;
            y = startY - (index * (itemHeight + spacing));
        }
        
        console.log('Getting static position for:', { category, index, x, y });
        return { x, y };
    };

    // 繪製 Canvas 圓形
    const drawCircles = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        const circles = getCircleConfig();
        const boysCircle = circles.boys;
        const girlsCircle = circles.girls;
        
        // 清除畫布
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // 繪製男孩圓形
        ctx.beginPath();
        ctx.arc(boysCircle.x, boysCircle.y, boysCircle.radius, 0, 2 * Math.PI);
        ctx.fillStyle = 'rgba(33, 150, 243, 0.1)';
        ctx.fill();
        ctx.strokeStyle = '#2196f3';
        ctx.lineWidth = 3;
        ctx.stroke();
        
        // 繪製女孩圓形
        ctx.beginPath();
        ctx.arc(girlsCircle.x, girlsCircle.y, girlsCircle.radius, 0, 2 * Math.PI);
        ctx.fillStyle = 'rgba(233, 30, 99, 0.1)';
        ctx.fill();
        ctx.strokeStyle = '#e91e63';
        ctx.lineWidth = 3;
        ctx.stroke();
        
        // 繪製重疊區域
        ctx.beginPath();
        ctx.arc(boysCircle.x, boysCircle.y, boysCircle.radius, 0, 2 * Math.PI);
        ctx.arc(girlsCircle.x, girlsCircle.y, girlsCircle.radius, 0, 2 * Math.PI);
        ctx.fillStyle = 'rgba(76, 175, 80, 0.1)';
        ctx.fill('evenodd');
        ctx.strokeStyle = '#4caf50';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.stroke();
        ctx.setLineDash([]);
        
        // 繪製標籤
        ctx.fillStyle = '#2196f3';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('男孩的變化', boysCircle.x, boysCircle.y - 15);
        
        ctx.fillStyle = '#e91e63';
        ctx.fillText('女孩的變化', girlsCircle.x, girlsCircle.y - 15);
        
        ctx.fillStyle = '#4caf50';
        ctx.font = 'bold 16px Arial';
        const overlapX = (boysCircle.x + girlsCircle.x) / 2;
        const overlapY = (boysCircle.y + girlsCircle.y) / 2;
        ctx.fillText('共同變化', overlapX, overlapY);
        
        // 添加調試信息 - 顯示圓形中心點和半徑
        ctx.fillStyle = 'red';
        ctx.font = '12px Arial';
        ctx.fillText(`B:(${Math.round(boysCircle.x)},${Math.round(boysCircle.y)}) R:${Math.round(boysCircle.radius)}`, boysCircle.x, boysCircle.y + 20);
        ctx.fillText(`G:(${Math.round(girlsCircle.x)},${Math.round(girlsCircle.y)}) R:${Math.round(girlsCircle.radius)}`, girlsCircle.x, girlsCircle.y + 20);
    };

    // 更新容器大小
    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                setContainerSize({ width: rect.width, height: rect.height });
            }
        };
        
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    // 繪製圓形
    useEffect(() => {
        if (containerSize.width > 0) {
            drawCircles();
        }
    }, [containerSize]);

    const handleMouseDown = (e: React.MouseEvent, item: DraggableItem) => {
        if (item.isPlaced) return; // 防止已配對的項目被拖拽
        
        e.preventDefault();
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        
        setDraggedItem(item);
        setDragOffset({
            x: e.clientX - rect.left - item.x,
            y: e.clientY - rect.top - item.y
        });
        
        setItems(prev => prev.map(i => 
            i.id === item.id ? { ...i, isDragging: true } : i
        ));
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!draggedItem || !containerRef.current) return;
        
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left - dragOffset.x;
        const y = e.clientY - rect.top - dragOffset.y;
        
        setItems(prev => prev.map(item => 
            item.id === draggedItem.id ? { ...item, x, y } : item
        ));
    };

    const handleMouseUp = () => {
        if (!draggedItem) return;
        
        console.log('Mouse up with item:', draggedItem.text, 'at position:', draggedItem.x, draggedItem.y);
        
        // 步驟1：檢查碰撞檢測
        const collision = checkCollision(draggedItem, draggedItem.x, draggedItem.y);
        const isOverlap = checkOverlap(draggedItem.x, draggedItem.y);
        
        console.log('Collision result:', collision);
        console.log('Is overlap:', isOverlap);
        
        let newPlacedIn: string | null = null;
        let isCorrect = false;
        
        // 步驟2：判斷配對邏輯
        if (collision) {
            if (isOverlap) {
                // 情況A：在重疊區域，只有共同變化（both）才正確
                if (draggedItem.category === 'both') {
                    newPlacedIn = 'both';
                    isCorrect = true;
                    console.log('Correct placement in overlap area for both category');
                } else {
                    console.log('Wrong category for overlap area - only "both" items belong here');
                }
            } else {
                // 情況B：在單一圓圈內，檢查類別是否匹配
                if (collision.circle === draggedItem.category) {
                    newPlacedIn = collision.circle;
                    isCorrect = true;
                    console.log('Correct placement in single circle:', collision.circle);
                } else {
                    console.log('Wrong category for circle:', collision.circle, 'expected:', draggedItem.category);
                }
            }
        } else {
            console.log('No collision detected - item is outside all circles');
        }
        
        console.log('Final result:', { isCorrect, newPlacedIn });
        
        // 步驟3：處理結果
        if (isCorrect) {
            // 正確配對：計算靜態位置並固定項目
            const placedItems = items.filter(item => item.isPlaced && item.placedIn === newPlacedIn);
            const staticPos = getStaticPosition(newPlacedIn!, placedItems.length);
            
            console.log('Placing item at static position:', staticPos);
            console.log('Current placed items count for', newPlacedIn, ':', placedItems.length);
            
            setItems(prev => prev.map(item => 
                item.id === draggedItem.id 
                    ? { 
                        ...item, 
                        isPlaced: true, 
                        placedIn: newPlacedIn, 
                        isDragging: false,
                        staticX: staticPos.x,
                        staticY: staticPos.y
                    }
                    : item
            ));
            setScore(prev => prev + 1);
        } else {
            // 錯誤配對：彈回原位
            console.log('Bouncing back to original position');
            setItems(prev => prev.map(item => 
                item.id === draggedItem.id 
                    ? { ...item, isDragging: false, x: 50 + ((item.id - 1) % 4) * 200, y: 50 + Math.floor((item.id - 1) / 4) * 80 }
                    : item
            ));
        }
        
        setDraggedItem(null);
        
        // 步驟4：檢查遊戲是否完成
        const placedCount = items.filter(item => item.isPlaced).length + (isCorrect ? 1 : 0);
        if (placedCount === teenChanges.length) {
            setGameComplete(true);
        }
    };

    const handleRestart = () => {
        setItems(teenChanges.map((item, index) => ({
            ...item,
            x: 50 + (index % 4) * 200,
            y: 50 + Math.floor(index / 4) * 80,
            isDragging: false,
            isPlaced: false,
            placedIn: null
        })));
        setScore(0);
        setGameComplete(false);
        setDraggedItem(null);
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 3 }}>
                🧬 青春期變化配對遊戲
            </Typography>
            
            <Typography variant="body1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
                將下方的青春期變化特徵拖拽到正確的圓圈內！
            </Typography>

            {/* 分數顯示 */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Chip 
                    label={`得分: ${score} / ${teenChanges.length}`} 
                    color="primary" 
                    variant="outlined"
                    sx={{ fontSize: '1.1rem', px: 2, py: 1 }}
                />
            </Box>

            {/* 遊戲區域 */}
            <Box 
                ref={containerRef}
                sx={{ 
                    position: 'relative', 
                    width: '100%', 
                    height: '700px', 
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    backgroundColor: '#fafafa',
                    overflow: 'hidden'
                }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                {/* Canvas 圓形 */}
                <canvas
                    ref={canvasRef}
                    width={containerSize.width}
                    height={containerSize.height}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                        pointerEvents: 'none'
                    }}
                />

                {/* 拖拽項目 */}
                {items.map((item) => {
                    const displayX = item.isPlaced && item.staticX !== undefined ? item.staticX : item.x;
                    const displayY = item.isPlaced && item.staticY !== undefined ? item.staticY : item.y;
                    
                    // 添加調試信息
                    if (item.isPlaced) {
                        console.log(`Item ${item.id} (${item.text}) placed at:`, { 
                            x: displayX, 
                            y: displayY, 
                            category: item.placedIn,
                            staticX: item.staticX,
                            staticY: item.staticY
                        });
                    }
                    
                    return (
                        <Paper
                            key={item.id}
                            sx={{
                                position: 'absolute',
                                left: displayX,
                                top: displayY,
                                p: 1,
                                backgroundColor: item.isPlaced ? item.color : 'white',
                                color: item.isPlaced ? 'white' : 'black',
                                border: `2px solid ${item.color}`,
                                borderRadius: 2,
                                cursor: item.isPlaced ? 'default' : 'grab',
                                userSelect: 'none',
                                zIndex: item.isDragging ? 1000 : 10,
                                transform: item.isDragging ? 'scale(1.1)' : 'scale(1)',
                                transition: item.isDragging ? 'none' : 'all 0.3s ease',
                                boxShadow: item.isDragging ? 4 : 2,
                                opacity: item.isPlaced ? 0.9 : 1,
                                minWidth: 120,
                                maxWidth: 120,
                                textAlign: 'center'
                            }}
                            onMouseDown={(e) => handleMouseDown(e, item)}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: '0.8rem' }}>
                                {item.text}
                            </Typography>
                        </Paper>
                    );
                })}
            </Box>

            {/* 控制按鈕 */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                    variant="contained"
                    onClick={handleRestart}
                    size="large"
                    sx={{ minWidth: 150 }}
                >
                    {gameComplete ? '再玩一次' : '重新開始'}
                </Button>
            </Box>

            {/* 完成遊戲訊息 */}
            {gameComplete && (
                <Card sx={{ mt: 3, backgroundColor: '#e8f5e8' }}>
                    <CardContent>
                        <Typography variant="h5" align="center" gutterBottom>
                            🎉 恭喜你完成遊戲！
                        </Typography>
                        <Typography variant="body1" align="center">
                            你成功將所有青春期變化特徵配對到正確的位置！
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}
